// O PAINEL DO ASSISTENTE DE IA — ticket 0045, a versão do HQ-Lab: "ajudar na criação de conteúdo,
// gerenciamento de algumas ações na plataforma".
//
// ── O QUE ELE FAZ, E O QUE ELE NUNCA FAZ ─────────────────────────────────────────────────────
//
// Conversa e mostra PROPOSTAS. Cada proposta tem um botão "Aprovar", e só esse botão chama o que
// já existe no app: preencher os campos da história, montar o roteiro (`/roteirizar`), gerar uma
// imagem (`/generate`, com a barreira de lá), abrir um rascunho privado, levar a uma tela.
//
// Ele NUNCA publica, nunca mexe em vínculo ou instituição e nunca lê quadrinho de outra pessoa:
// essas ações não estão no catálogo do servidor, e uma ação desconhecida que chegasse aqui não tem
// botão — cai no `default` e não aparece.
//
// ── QUANDO ELE NÃO APARECE ───────────────────────────────────────────────────────────────────
//
// Sem chave no servidor, o painel não é desenhado: a tela não convida para uma conversa que vai
// terminar em "ainda não ligado". Sem conta, ele aparece fechado e diz que precisa entrar.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ENDERECO_DA_TELA,
  FalaDoAssistente,
  ModoDoAssistente,
  PropostaDeAcao,
  PropostasDoAssistente,
  TIPO_DA_IMAGEM,
  assistenteDisponivel,
  conversar,
} from "../api/assistente";
import Imgen, { ConteudoRecusadoError } from "../api/imgen";

import "./painel-do-assistente.scss";

export interface AoAprovar {
  /** Os campos da história propostos pelo assistente (título, texto). */
  formulario?: (campos: { titulo?: string; historia?: string }) => void;
  /** Montar o roteiro com o texto proposto — chama o mesmo trabalho do botão "Montar". */
  roteirizar?: (pedido: { titulo?: string; texto: string }) => void;
  /** Abrir um rascunho privado com este título. */
  criarRascunho?: (titulo: string) => void;
}

interface Props {
  /** Em que tela está, para o modo "agir". */
  tela: string;
  /** O que a pessoa já escreveu, para o assistente aproveitar as palavras dela. */
  rascunho?: string;
  logado: boolean;
  aoAprovar: AoAprovar;
}

const MODOS: { id: ModoDoAssistente; rotulo: string }[] = [
  { id: "criar-historia", rotulo: "Criar a história" },
  { id: "criar-personagem", rotulo: "Criar um personagem" },
  { id: "agir", rotulo: "Fazer algo no HQ-Lab" },
  { id: "ajuda", rotulo: "Como funciona" },
];

const ROTULO_DA_ACAO: Record<string, string> = {
  roteirizar: "Montar o quadrinho com esta história",
  gerar_imagem: "Gerar esta imagem",
  criar_rascunho: "Abrir um rascunho com este título",
  abrir_tela: "Ir para esta tela",
};

interface Mensagem extends FalaDoAssistente {
  propostas?: PropostasDoAssistente;
}

const texto = (v: unknown) => (typeof v === "string" ? v : "");

export default function PainelDoAssistente({ tela, rascunho, logado, aoAprovar }: Props) {
  const navigate = useNavigate();
  const [disponivel, setDisponivel] = useState<boolean | null>(null);
  const [aberto, setAberto] = useState(false);
  const [modo, setModo] = useState<ModoDoAssistente>("criar-historia");
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [escrevendo, setEscrevendo] = useState("");
  const [esperando, setEsperando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [imagem, setImagem] = useState<{ estado: "gerando" | "pronta" | "recusada" | "falhou"; url?: string; frase?: string } | null>(null);
  const fim = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let vivo = true;
    assistenteDisponivel().then((d) => vivo && setDisponivel(d));
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    fim.current?.scrollIntoView?.({ block: "nearest" });
  }, [mensagens, esperando]);

  if (!disponivel) return null;

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    const fala = escrevendo.trim();
    if (!fala || esperando) return;
    const historico: Mensagem[] = [...mensagens, { papel: "pessoa", texto: fala }];
    setMensagens(historico);
    setEscrevendo("");
    setAviso(null);
    setEsperando(true);
    const r = await conversar({
      modo,
      contexto: modo === "agir" ? { tela } : { rascunho: (rascunho ?? "").slice(0, 3000) },
      falas: historico.slice(-12).map(({ papel, texto }) => ({ papel, texto })),
    });
    setEsperando(false);
    if (!r.ok) {
      setAviso(r.aviso ?? null);
      return;
    }
    setMensagens([...historico, { papel: "assistente", texto: r.texto, propostas: r.propostas }]);
  }

  async function gerarImagem(descricao: string, tipo: string) {
    setImagem({ estado: "gerando" });
    try {
      const r = await Imgen.generateImages(descricao, undefined, undefined, TIPO_DA_IMAGEM[tipo] ?? null);
      setImagem({ estado: "pronta", url: r.images[0] });
    } catch (e) {
      if (e instanceof ConteudoRecusadoError) setImagem({ estado: "recusada", frase: e.message });
      else setImagem({ estado: "falhou", frase: "A imagem não saiu agora. Tente de novo em alguns minutos." });
    }
  }

  /** O ÚNICO lugar em que uma proposta vira gesto — e só no clique da pessoa. */
  function aprovarAcao(a: PropostaDeAcao) {
    const arg = a.argumentos;
    switch (a.acao) {
      case "roteirizar":
        if (texto(arg.texto)) aoAprovar.roteirizar?.({ titulo: texto(arg.titulo) || undefined, texto: texto(arg.texto) });
        return;
      case "gerar_imagem":
        if (texto(arg.descricao)) gerarImagem(texto(arg.descricao), texto(arg.tipo));
        return;
      case "criar_rascunho":
        if (aoAprovar.criarRascunho) aoAprovar.criarRascunho(texto(arg.titulo));
        else navigate("/meus-quadrinhos/novo");
        return;
      case "abrir_tela": {
        const destino = ENDERECO_DA_TELA[texto(arg.tela)];
        if (destino) navigate(destino);
        return;
      }
      default:
        // Ação fora do catálogo: não tem botão, e não chega aqui por clique.
        return;
    }
  }

  function podeAprovar(a: PropostaDeAcao): boolean {
    if (a.acao === "roteirizar") return !!aoAprovar.roteirizar;
    if (a.acao === "abrir_tela") return !!ENDERECO_DA_TELA[texto(a.argumentos.tela)];
    return a.acao in ROTULO_DA_ACAO;
  }

  function resumoDaAcao(a: PropostaDeAcao): string {
    const arg = a.argumentos;
    if (a.acao === "roteirizar") return [texto(arg.titulo), texto(arg.texto)].filter(Boolean).join(" — ");
    if (a.acao === "gerar_imagem") return texto(arg.descricao);
    if (a.acao === "criar_rascunho") return texto(arg.titulo);
    if (a.acao === "abrir_tela") return texto(arg.tela);
    return "";
  }

  return (
    <section className="painel-assistente" aria-label="Assistente de IA">
      <button
        type="button"
        className="painel-assistente-abrir"
        aria-expanded={aberto}
        onClick={() => setAberto((x) => !x)}
      >
        {aberto ? "Fechar o assistente" : "Pedir ajuda ao assistente"}
      </button>

      {aberto && !logado && (
        <p className="painel-assistente-aviso" role="status">
          Entre na sua conta para conversar com o assistente.
        </p>
      )}

      {aberto && logado && (
        <div className="painel-assistente-corpo">
          <p className="painel-assistente-explica">
            O assistente sugere; nada acontece sem você clicar em <strong>Aprovar</strong>. Ele
            não publica o seu quadrinho — publicar é sempre com o seu botão.
          </p>

          <div className="painel-assistente-modos" role="group" aria-label="O que você quer fazer">
            {MODOS.map((m) => (
              <button
                key={m.id}
                type="button"
                aria-pressed={modo === m.id}
                className={modo === m.id ? "ativo" : ""}
                onClick={() => setModo(m.id)}
              >
                {m.rotulo}
              </button>
            ))}
          </div>

          <div className="painel-assistente-conversa" aria-live="polite">
            {mensagens.map((m, i) => (
              <div key={i} className={`painel-assistente-fala ${m.papel}`}>
                {m.texto && <p>{m.texto}</p>}

                {m.propostas?.formulario && (
                  <div className="painel-assistente-proposta">
                    {m.propostas.formulario.titulo && (
                      <p>
                        <strong>Título:</strong> {m.propostas.formulario.titulo}
                      </p>
                    )}
                    {m.propostas.formulario.historia && (
                      <p className="painel-assistente-historia">{m.propostas.formulario.historia}</p>
                    )}
                    {aoAprovar.formulario && (
                      <button
                        type="button"
                        onClick={() =>
                          aoAprovar.formulario?.({
                            titulo: m.propostas?.formulario?.titulo,
                            historia: m.propostas?.formulario?.historia,
                          })
                        }
                      >
                        Aprovar: pôr nos campos
                      </button>
                    )}
                  </div>
                )}

                {m.propostas?.acoes?.filter(podeAprovar).map((a, j) => (
                  <div key={j} className="painel-assistente-proposta">
                    <p>
                      <strong>{ROTULO_DA_ACAO[a.acao]}</strong>
                      {resumoDaAcao(a) && <>: {resumoDaAcao(a)}</>}
                    </p>
                    {a.porque && <p className="painel-assistente-porque">{a.porque}</p>}
                    <button type="button" onClick={() => aprovarAcao(a)}>
                      Aprovar
                    </button>
                  </div>
                ))}
              </div>
            ))}
            {esperando && <p className="painel-assistente-esperando">O assistente está pensando…</p>}
            <div ref={fim} />
          </div>

          {imagem && (
            <div className="painel-assistente-imagem" role="status">
              {imagem.estado === "gerando" && <p>Gerando a imagem…</p>}
              {imagem.estado === "pronta" && imagem.url && <img src={imagem.url} alt="Imagem gerada a partir da sugestão" />}
              {(imagem.estado === "recusada" || imagem.estado === "falhou") && <p>{imagem.frase}</p>}
            </div>
          )}

          {aviso && (
            <p className="painel-assistente-aviso" role="status">
              {aviso}
            </p>
          )}

          <form className="painel-assistente-escrever" onSubmit={enviar}>
            <label htmlFor="fala-do-assistente">Sua mensagem</label>
            <textarea
              id="fala-do-assistente"
              rows={2}
              maxLength={2000}
              value={escrevendo}
              onChange={(e) => setEscrevendo(e.target.value)}
              disabled={esperando}
            />
            <button type="submit" disabled={esperando || !escrevendo.trim()}>
              Enviar
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
